import * as React from "react"
import { router } from "@inertiajs/react"
import { Loader2, Telescope, X } from "lucide-react"
import { consumer } from "@/lib/actioncable"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export type ResearchRun = {
  id: number
  status: string
  signals_count: number
  error_message: string | null
  created_at: string
}

export type ResearchState = {
  available: boolean
  latest_run: ResearchRun | null
}

type Broadcast = {
  run_id: number
  status: string
  type: string
  signals_count?: number
  error_message?: string | null
}

const RUNNING = new Set(["pending", "running"])

const STATUS_LABELS: Record<string, string> = {
  pending: "Antri",
  running: "Berjalan",
  completed: "Selesai",
  failed: "Gagal",
  cancelled: "Dibatalkan",
}

// Deep-research trigger for one manufacturer. Unlike the #39 account panel there
// is no domain picker — M2 issues a SINGLE holistic sonar-deep-research pass per
// run (DOMAINS is only a coverage checklist + per-signal label). The run takes
// minutes and is pricier than plain Sonar, so triggering goes through a confirm.
export function ResearchPanel({
  manufacturerId,
  research,
  pendingResearchCount,
}: {
  manufacturerId: number
  research: ResearchState
  pendingResearchCount: number
}) {
  const [run, setRun] = React.useState<ResearchRun | null>(research.latest_run)
  const [liveCount, setLiveCount] = React.useState<number>(research.latest_run?.signals_count ?? 0)
  const [failMessage, setFailMessage] = React.useState<string | null>(
    research.latest_run?.status === "failed" ? (research.latest_run.error_message ?? null) : null,
  )
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  // Keep local state in sync when the server ships a fresh latest_run (e.g. after
  // the create redirect reloads props).
  React.useEffect(() => {
    setRun(research.latest_run)
    setLiveCount(research.latest_run?.signals_count ?? 0)
  }, [research.latest_run])

  const watching = run && RUNNING.has(run.status) ? run.id : null

  // Live progress over ManufacturerResearchChannel (mirrors the #39 flow). The
  // subscription lives in an effect, so it never runs during SSR.
  React.useEffect(() => {
    if (!watching) return
    const sub = consumer.subscriptions.create(
      { channel: "ManufacturerResearchChannel", run_id: watching },
      {
        received(data: Broadcast) {
          if (data.run_id !== watching) return
          if (typeof data.signals_count === "number") setLiveCount(data.signals_count)
          switch (data.type) {
            case "research_failed":
              setFailMessage(data.error_message ?? "Terjadi kesalahan.")
              setRun((r) => (r ? { ...r, status: "failed" } : r))
              break
            case "research_complete":
            case "research_cancelled":
              setRun((r) => (r ? { ...r, status: data.status } : r))
              // Surface the new pending signals + run metadata.
              router.reload({ only: ["signals", "research"] })
              break
            default:
              setRun((r) => (r ? { ...r, status: data.status } : r))
          }
        },
      },
    )
    return () => {
      sub.unsubscribe()
    }
  }, [watching])

  const isRunning = run != null && RUNNING.has(run.status)

  const startRun = () => {
    setFailMessage(null)
    setConfirmOpen(false)
    router.post(
      `/admin/manufacturers/${manufacturerId}/research_runs`,
      {},
      { preserveScroll: true },
    )
  }

  const cancelRun = () => {
    if (!run) return
    router.patch(
      `/admin/manufacturers/${manufacturerId}/research_runs/${run.id}/cancel`,
      {},
      { preserveScroll: true },
    )
  }

  if (!research.available) {
    return (
      <section className="mt-6 rounded-lg border border-dashed border-hairline bg-surface p-5">
        <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
          <Telescope className="h-4 w-4" />
          Riset Mendalam
        </div>
        <p className="mt-2 text-sm text-ink-muted">
          Mesin riset mendalam sedang nonaktif. Aktifkan dengan menyetel{" "}
          <code className="text-xs">MANUFACTURER_RESEARCH=true</code> dan kredensial{" "}
          <code className="text-xs">openrouter_api_key</code>. Entri manual & semua tampilan tetap
          berfungsi.
        </p>
      </section>
    )
  }

  return (
    <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
            <Telescope className="h-4 w-4" />
            Riset Mendalam
          </div>
          <p className="mt-0.5 text-sm text-ink-muted">
            Jalankan satu riset 360° mendalam (Perplexity sonar-deep-research). Hasil masuk sebagai
            sinyal <strong>menunggu review</strong>. Riset ini memakan beberapa menit.
          </p>
        </div>
        {pendingResearchCount > 0 && (
          <Badge tone="signal">{pendingResearchCount} sinyal menunggu review</Badge>
        )}
      </div>

      {/* Live status */}
      {run && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-page p-3 text-sm">
          {isRunning && <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />}
          <span className="text-ink-body">
            Riset terakhir: <strong>{STATUS_LABELS[run.status] ?? run.status}</strong>
          </span>
          <Badge tone="muted">{liveCount} sinyal</Badge>
          {run.status === "failed" && failMessage && (
            <span className="text-danger">· {failMessage}</span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button onClick={() => setConfirmOpen(true)} disabled={isRunning}>
          <Telescope className="mr-2 h-4 w-4" />
          Jalankan riset mendalam
        </Button>
        {isRunning && (
          <Button variant="ghost" onClick={cancelRun}>
            <X className="mr-1 h-4 w-4" />
            Batalkan
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Jalankan riset mendalam?</DialogTitle>
            <DialogDescription>
              Riset mendalam (sonar-deep-research) memakan waktu beberapa menit dan berbiaya lebih
              tinggi dari riset biasa. Hasilnya masuk sebagai sinyal menunggu review.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Batal
            </Button>
            <Button onClick={startRun}>
              <Telescope className="mr-2 h-4 w-4" />
              Jalankan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
